import { Facebook, Instagram, LinkedIn, Twitter, Web } from '@air/icons';
import { Box, IconButton, Text } from '@air/zephyr';
import { RichText } from '@graphcms/rich-text-react-renderer';
import { find } from 'lodash';
import Image from 'next/image';

import { AgencyData, ImageData } from '~/old-marketing/components/types/custom-types';
import { RichText as RichTextType } from '~/old-marketing/components/types/generated-types';
import { AgencySizes } from '~/old-marketing/constants/AgencySizes';
import { formatAgencyTypes } from '~/old-marketing/utils/formatAgencyTypes';

export interface AgencyInformationProps extends Omit<AgencyData, 'description' | 'id'> {
  logo?: ImageData;
  description: RichTextType['raw'];
}

export const AgencyInformation = ({
  clientLogos,
  companySize,
  description,
  facebookUrl,
  instagramUrl,
  linkedInUrl,
  location,
  logo,
  name,
  twitterUrl,
  types,
  websiteUrl,
}: AgencyInformationProps) => {
  return (
    <Box as="article" tx={{ color: 'pigeon700' }}>
      {logo && logo.height && logo.width && (
        <Box
          tx={{
            position: 'relative',
            borderRadius: 8,
            pb: '48%',
            mb: 40,
            overflow: 'hidden',

            img: {
              objectFit: 'contain',
            },
          }}
        >
          <Image layout="fill" src={logo.url} />
        </Box>
      )}

      <Text as="h1" tx={{ mb: 24, color: 'inherit' }} variant="text-ui-32">
        {name}
      </Text>

      {(facebookUrl || instagramUrl || linkedInUrl || twitterUrl || websiteUrl) && (
        <Box tx={{ display: 'flex', mb: 32 }}>
          {websiteUrl && (
            <IconButton
              as="a"
              href={websiteUrl}
              icon={Web}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              tx={{ mr: 8 }}
              variant="button-ghost-grey"
            >
              {`${name} website`}
            </IconButton>
          )}

          {facebookUrl && (
            <IconButton
              as="a"
              href={facebookUrl}
              icon={Facebook}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              tx={{ mr: 8 }}
              variant="button-ghost-grey"
            >
              Facebook
            </IconButton>
          )}

          {twitterUrl && (
            <IconButton
              as="a"
              href={twitterUrl}
              icon={Twitter}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              tx={{ mr: 8 }}
              variant="button-ghost-grey"
            >
              Twitter
            </IconButton>
          )}

          {linkedInUrl && (
            <IconButton
              as="a"
              href={linkedInUrl}
              icon={LinkedIn}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              tx={{ mr: 8 }}
              variant="button-ghost-grey"
            >
              LinkedIn
            </IconButton>
          )}

          {instagramUrl && (
            <IconButton
              as="a"
              href={instagramUrl}
              icon={Instagram}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              tx={{ mr: 8 }}
              variant="button-ghost-grey"
            >
              Instagram
            </IconButton>
          )}
        </Box>
      )}

      {types && (
        <Text as="p" tx={{ mb: 8, color: 'inherit' }} variant="text-ui-18">
          Type: {formatAgencyTypes(types)}
        </Text>
      )}

      {companySize && (
        <Text as="p" tx={{ mb: 8, color: 'inherit' }} variant="text-ui-18">
          Size: {find(AgencySizes, ['value', companySize])?.name}
        </Text>
      )}

      {location && (
        <Text as="p" tx={{ mb: 24, color: 'inherit' }} variant="text-ui-18">
          Location: {location.map((location) => location.replace(',', ', ')).join(', ')}
        </Text>
      )}

      {description && (
        <Text tx={{ mb: 40 }} variant="text-ui-18">
          <RichText content={description} />
        </Text>
      )}

      <Text as="h2" tx={{ mb: 32, color: 'inherit', fontWeight: 'semibold' }} variant="text-ui-24">
        Clients include
      </Text>

      {clientLogos && (
        <Box tx={{ display: 'grid', gridGap: 48, gridTemplateColumns: 'repeat(3, 1fr)', mb: 40 }}>
          {clientLogos.map((logo) => {
            if (!(logo && logo.height && logo.width)) {
              return null;
            }

            return (
              <Box key={logo.id} tx={{ position: 'relative', height: 72, img: { objectFit: 'contain' } }}>
                <Image layout="fill" src={logo.url} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
